<!-- 
  DailyStatistics.vue
  일자별 통계 페이지
  
  - 일자별 상세 통계 표시
  - 엑셀 형식 테이블
-->
<template>
  <div class="container-fluid col-sm-12 col-xs-12">
    <div style="margin-top: 28px"></div>
    <!-- 페이지 헤더 영역 -->
    <div class="card-header pb-0 col-sm-12 d-flex">
      <div class="col-sm-12 col-xs-12">
        <div class="col-sm-12 col-xs-12 justify-content-between titleContainer">
          <div class="title-row">
            <div>
              <h1 class="fw-bold page-title">
                일자별 통계
              </h1>
              <span class="pageDesc">이벤트의 일자별 상세 통계를 확인할 수 있습니다.</span>
            </div>
            <div class="header-actions">
              <div class="date-filter">
                <a-range-picker
                  v-model:value="dateRange"
                  :placeholder="['시작일', '종료일']"
                  format="YYYY-MM-DD"
                  @change="handleDateChange"
                  :allow-clear="false"
                />
                <a-button type="primary" @click="fetchDailyStats" :loading="loading">
                  조회
                </a-button>
                <a-button @click="exportExcel" :disabled="!dailyStats.length">
                  <i class="fa fa-download" style="margin-right: 4px"></i>
                  엑셀 다운로드
                </a-button>
              </div>
              <a-button class="back-button" @click="goBack">
                <i class="fa fa-arrow-left" style="margin-right: 5px"></i> 뒤로가기
              </a-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 콘텐츠 영역 -->
    <div class="card-body px-0 pt-3 pb-2">
      <div class="card content-wrap-card">
        <div v-if="loading" class="loading-container">
          <a-spin size="large" />
          <p>데이터를 불러오는 중입니다...</p>
        </div>
        
        <div v-else-if="dailyStats.length > 0" class="table-container">
                      <a-table
              :columns="columns"
              :data-source="dailyStats"
              :pagination="false"
              :scroll="{ x: 2400, y: 'calc(100vh - 400px)' }"
              size="small"
              bordered
              sticky
              :row-key="record => record._id"
            >
            <template #bodyCell="{ column, text, record }">
              <template v-if="column.dataIndex === 'statsDate'">
                <span class="date-cell" :class="getDayOfWeekClass(record.dayOfWeek)">
                  {{ formatDate(text) }}({{ record.dayOfWeek || '' }})
                </span>
              </template>
              <template v-else-if="column.key && column.key.includes('EntryRate')">
                <span :class="getEntryRateClass(text)">{{ formatPercent(text) }}</span>
              </template>
              <template v-else-if="column.key && column.key.includes('Rate')">
                <span>{{ formatPercent(text) }}</span>
              </template>
              <template v-else-if="column.key === 'smileShareUserCount'">
                <span>{{ typeof text === 'number' ? text.toFixed(1) : text }}</span>
              </template>
              <template v-else-if="typeof text === 'number'">
                <span>{{ text.toLocaleString() }}</span>
              </template>
            </template>
            <template #summary>
              <a-table-summary>
                <a-table-summary-row>
                  <a-table-summary-cell :index="0" class="summary-cell">
                    <strong>합계/평균</strong>
                  </a-table-summary-cell>
                  <a-table-summary-cell v-for="(value, index) in summaryData" :key="index" :index="index + 1" class="summary-cell">
                    <strong>{{ value }}</strong>
                  </a-table-summary-cell>
                </a-table-summary-row>
              </a-table-summary>
            </template>
          </a-table>
        </div>
        
        <div v-else class="no-data-container">
          <i class="fa fa-chart-line no-data-icon"></i>
          <h3>데이터가 없습니다</h3>
          <p>선택한 기간에 대한 통계 데이터가 없습니다.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import axios from '@/axios';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

export default {
  name: "DailyStatistics",
  setup() {
    const router = useRouter();
    const loading = ref(false);
    const dailyStats = ref([]);
    const dateRange = ref([dayjs('2025-07-03'), dayjs('2025-08-31')]);
    const eventId = '6864f97c93111a68978dc3f5';
    
    // 합계/평균 데이터 계산
    const summaryData = computed(() => {
      if (!dailyStats.value.length) return [];
      
      const sum = {
        totalSendCount: 0,
        totalParticipantCount: 0,
        totalShareCount: 0,
        totalHospitalInquiry: 0,
        totalEntryRate: [],
        totalReviewCompleteCount: 0,
        smileSendCount: 0,
        smileParticipantCount: 0,
        smileShareCount: 0,
        smileEntryRate: [],
        smileShareUserCount: [],
        smileHospitalInquiry: 0,
        friendSendCount: 0,
        friendParticipantCount: 0,
        friendShareCount: 0,
        friendEntryRate: [],
        friendHospitalInquiry: 0,
        friendBookingClick: 0,
        friendHospitalBooking: 0,
        fofSendCount: 0,
        fofParticipantCount: 0,
        fofShareCount: 0,
        fofEntryRate: [],
        fofHospitalInquiry: 0,
        fofBookingClick: 0,
        fofHospitalBooking: 0
      };
      
      dailyStats.value.forEach(item => {
        // 전체 통계
        sum.totalSendCount += item.totalStats?.sendCount || 0;
        sum.totalParticipantCount += item.totalStats?.participantCount || 0;
        sum.totalShareCount += item.totalStats?.shareCount || 0;
        sum.totalHospitalInquiry += item.totalStats?.hospitalInquiryCount || 0;
        if (item.totalStats?.entryRate) sum.totalEntryRate.push(item.totalStats.entryRate);
        sum.totalReviewCompleteCount += item.totalStats?.reviewCompleteCount || 0;
        
        // 시술받은 환자
        sum.smileSendCount += item.smilePatient?.sendCount || 0;
        sum.smileParticipantCount += item.smilePatient?.participantCount || 0;
        sum.smileShareCount += item.smilePatient?.shareCount || 0;
        if (item.smilePatient?.entryRate) sum.smileEntryRate.push(item.smilePatient.entryRate);
        if (item.smilePatient?.shareUserCount !== undefined && item.smilePatient?.shareUserCount !== null) {
          sum.smileShareUserCount.push(item.smilePatient.shareUserCount);
        }
        sum.smileHospitalInquiry += item.smilePatient?.hospitalInquiryCount || 0;
        
        // 환자의 친구
        sum.friendSendCount += item.patientFriend?.sendCount || 0;
        sum.friendParticipantCount += item.patientFriend?.participantCount || 0;
        sum.friendShareCount += item.patientFriend?.shareCount || 0;
        if (item.patientFriend?.entryRate) sum.friendEntryRate.push(item.patientFriend.entryRate);
        sum.friendHospitalInquiry += item.patientFriend?.hospitalInquiryCount || 0;
        sum.friendBookingClick += item.patientFriend?.bookingClickCount || 0;
        sum.friendHospitalBooking += item.patientFriend?.hospitalBookingCount || 0;
        
        // 친구의 친구
        sum.fofSendCount += item.friendOfFriend?.sendCount || 0;
        sum.fofParticipantCount += item.friendOfFriend?.participantCount || 0;
        sum.fofShareCount += item.friendOfFriend?.shareCount || 0;
        if (item.friendOfFriend?.entryRate) sum.fofEntryRate.push(item.friendOfFriend.entryRate);
        sum.fofHospitalInquiry += item.friendOfFriend?.hospitalInquiryCount || 0;
        sum.fofBookingClick += item.friendOfFriend?.bookingClickCount || 0;
        sum.fofHospitalBooking += item.friendOfFriend?.hospitalBookingCount || 0;
      });
      
      // 평균 계산 함수
      const calcAverage = (arr) => {
        if (!arr.length) return 0;
        const validNumbers = arr.filter(val => val != null && !isNaN(val));
        if (!validNumbers.length) return 0;
        return validNumbers.reduce((a, b) => Number(a) + Number(b), 0) / validNumbers.length;
      };
      
      return [
        // 전체
        `${sum.totalSendCount.toLocaleString()}건`,
        `${sum.totalParticipantCount.toLocaleString()}명`,
        `${sum.totalShareCount.toLocaleString()}건`,
        `${sum.totalHospitalInquiry.toLocaleString()}건`,
        formatPercent(calcAverage(sum.totalEntryRate)),
        // 시술받은 환자
        `${sum.smileSendCount.toLocaleString()}건`,
        `${sum.smileParticipantCount.toLocaleString()}명`,
        `${sum.smileShareCount.toLocaleString()}건`,
        `${sum.totalReviewCompleteCount.toLocaleString()}명`, // 후기 작성자
        `${sum.smileHospitalInquiry.toLocaleString()}건`,
        formatPercent(calcAverage(sum.smileEntryRate)),
        `${calcAverage(sum.smileShareUserCount).toFixed(1)}회`,
        // 환자의 친구
        `${sum.friendSendCount.toLocaleString()}건`,
        `${sum.friendParticipantCount.toLocaleString()}명`,
        `${sum.friendShareCount.toLocaleString()}건`,
        `${sum.friendHospitalInquiry.toLocaleString()}건`,
        `${sum.friendBookingClick.toLocaleString()}건`,
        `${sum.friendHospitalBooking.toLocaleString()}건`,
        // formatPercent(calcAverage(sum.friendEntryRate)),
        // 친구의 친구
        `${sum.fofSendCount.toLocaleString()}건`,
        `${sum.fofParticipantCount.toLocaleString()}명`,
        `${sum.fofShareCount.toLocaleString()}건`,
        `${sum.fofHospitalInquiry.toLocaleString()}건`,
        `${sum.fofBookingClick.toLocaleString()}건`,
        `${sum.fofHospitalBooking.toLocaleString()}건`
      ];
    });
    
    // 테이블 컬럼 정의
    const columns = [
      {
        title: '일자',
        dataIndex: 'statsDate',
        key: 'statsDate',
        width: 100,
        fixed: 'left',
        sorter: (a, b) => dayjs(a.statsDate).unix() - dayjs(b.statsDate).unix()
      },
      {
        title: '전체',
        children: [
          {
            title: '발송 건수',
            dataIndex: ['totalStats', 'sendCount'],
            key: 'totalSendCount',
            width: 90,
            align: 'right'
          },
          {
            title: '참여자 수',
            dataIndex: ['totalStats', 'participantCount'],
            key: 'totalParticipantCount',
            width: 90,
            align: 'right'
          },
          {
            title: '공유 횟수',
            dataIndex: ['totalStats', 'shareCount'],
            key: 'totalShareCount',
            width: 90,
            align: 'right'
          },
          {
            title: '병원 알아보기',
            dataIndex: ['totalStats', 'hospitalInquiryCount'],
            key: 'totalHospitalInquiry',
            width: 90,
            align: 'right'
          },
          {
            title: '응모률',
            dataIndex: ['totalStats', 'entryRate'],
            key: 'totalEntryRate',
            width: 80,
            align: 'right'
          }
        ]
      },
      {
        title: '시술받은 환자',
        children: [
          {
            title: '발송 건수',
            dataIndex: ['smilePatient', 'sendCount'],
            key: 'smileSendCount',
            width: 90,
            align: 'right'
          },
          {
            title: '참여자 수',
            dataIndex: ['smilePatient', 'participantCount'],
            key: 'smileParticipantCount',
            width: 90,
            align: 'right'
          },
          {
            title: '공유 횟수',
            dataIndex: ['smilePatient', 'shareCount'],
            key: 'smileShareCount',
            width: 90,
            align: 'right'
          },
          {
            title: '후기 작성자',
            dataIndex: ['totalStats', 'reviewCompleteCount'],
            key: 'totalShareCount',
            width: 90,
            align: 'right'
          },
          {
            title: '병원 알아보기',
            dataIndex: ['smilePatient', 'hospitalInquiryCount'],
            key: 'smileHospitalInquiry',
            width: 90,
            align: 'right'
          },
          {
            title: '응모률',
            dataIndex: ['smilePatient', 'entryRate'],
            key: 'smileEntryRate',
            width: 80,
            align: 'right'
          },
          {
            title: '공유건수(인당)',
            dataIndex: ['smilePatient', 'shareUserCount'],
            key: 'smileShareUserCount',
            width: 90,
            align: 'right'
          },
        ]
      },
      {
        title: '환자의 친구',
        children: [
          {
            title: '발송 건수',
            dataIndex: ['patientFriend', 'sendCount'],
            key: 'friendSendCount',
            width: 90,
            align: 'right'
          },
          {
            title: '참여자 수',
            dataIndex: ['patientFriend', 'participantCount'],
            key: 'friendParticipantCount',
            width: 90,
            align: 'right'
          },
          {
            title: '공유 횟수',
            dataIndex: ['patientFriend', 'shareCount'],
            key: 'friendShareCount',
            width: 90,
            align: 'right'
          },
          {
            title: '병원 알아보기',
            dataIndex: ['patientFriend', 'hospitalInquiryCount'],
            key: 'friendHospitalInquiry',
            width: 90,
            align: 'right'
          },
          {
            title: '예약 클릭',
            dataIndex: ['patientFriend', 'bookingClickCount'],
            key: 'friendBookingClick',
            width: 90,
            align: 'right'
          },
          {
            title: '병원 예약',
            dataIndex: ['patientFriend', 'hospitalBookingCount'],
            key: 'hospitalBooking',
            width: 90,
            align: 'right'
          }
          // {
          //   title: '응모률',
          //   dataIndex: ['patientFriend', 'entryRate'],
          //   key: 'friendEntryRate',
          //   width: 80,
          //   align: 'right'
          // },
        ]
      },
      {
        title: '친구의 친구',
        children: [
          {
            title: '발송 건수',
            dataIndex: ['friendOfFriend', 'sendCount'],
            key: 'fofSendCount',
            width: 90,
            align: 'right'
          },
          {
            title: '참여자 수',
            dataIndex: ['friendOfFriend', 'participantCount'],
            key: 'fofParticipantCount',
            width: 90,
            align: 'right'
          },
          {
            title: '공유 횟수',
            dataIndex: ['friendOfFriend', 'shareCount'],
            key: 'fofShareCount',
            width: 90,
            align: 'right'
          },
          {
            title: '병원 알아보기',
            dataIndex: ['friendOfFriend', 'hospitalInquiryCount'],
            key: 'fofHospitalInquiry',
            width: 90,
            align: 'right'
          },
          {
            title: '예약 클릭',
            dataIndex: ['friendOfFriend', 'bookingClickCount'],
            key: 'fofBookingClick',
            width: 90,
            align: 'right'
          },
          {
            title: '병원 예약',
            dataIndex: ['friendOfFriend', 'hospitalBookingCount'],
            key: 'hospitalBooking',
            width: 90,
            align: 'right'
          }
          // {
          //   title: '응모률',
          //   dataIndex: ['friendOfFriend', 'entryRate'],
          //   key: 'fofEntryRate',
          //   width: 80,
          //   align: 'right'
          // },
        ]
      },
    ];
    
    // 날짜 포맷
    const formatDate = (date) => {
      return dayjs(date).format('YYYY-MM-DD');
    };
    
    // 퍼센트 포맷
    const formatPercent = (value) => {
      if (!value || value === 0) return '0%';
      // 값이 1보다 작으면 비율로 간주하고 100을 곱함
      const percentValue = value < 1 ? value * 100 : value;
      return `${percentValue.toFixed(1)}%`;
    };
    
    // 응모률 클래스
    const getEntryRateClass = (rate) => {
      if (!rate) return '';
      const value = rate < 1 ? rate * 100 : rate;
      if (value >= 20) return 'high-rate';
      if (value >= 10) return 'medium-rate';
      return 'low-rate';
    };
    
    // 요일 클래스
    const getDayOfWeekClass = (dayOfWeek) => {
      if (dayOfWeek === '토') return 'saturday';
      if (dayOfWeek === '일') return 'sunday';
      return 'weekday';
    };
    
    // 일자별 통계 조회
    const fetchDailyStats = async () => {
      loading.value = true;
      
      try {
        const params = {
          startDate: dateRange.value[0].format('YYYY-MM-DD'),
          endDate: dateRange.value[1].format('YYYY-MM-DD')
        };
        
        const response = await axios.get(`/events-v2/statistics/${eventId}`, { params });
        
        if (response.data && response.data.data) {
          // 백엔드에서 데이터 객체로 응답하는 경우
          dailyStats.value = response.data.data;
          message.success(`${dailyStats.value.length}개의 일자별 통계를 불러왔습니다.`);
        } else if (response.data && Array.isArray(response.data)) {
          // 백엔드에서 배열로만 응답하는 경우
          dailyStats.value = response.data;
          message.success(`${dailyStats.value.length}개의 일자별 통계를 불러왔습니다.`);
        } else {
          message.error('데이터를 불러오는데 실패했습니다.');
        }
      } catch (error) {
        console.error('일자별 통계 조회 실패:', error);
        message.error('데이터를 불러오는 중 오류가 발생했습니다.');
      } finally {
        loading.value = false;
      }
    };
    
    // 날짜 변경 핸들러
    const handleDateChange = () => {
      if (dateRange.value && dateRange.value.length === 2) {
        fetchDailyStats();
      }
    };
    
    // 엑셀 다운로드
    const exportExcel = () => {
      if (!dailyStats.value.length) return;
      
      // 엑셀 데이터 준비
      const excelData = dailyStats.value.map(item => ({
        '일자': formatDate(item.statsDate),
        '전체_발송건수': item.totalStats?.sendCount || 0,
        '전체_참여자수': item.totalStats?.participantCount || 0,
        '전체_공유횟수': item.totalStats?.shareCount || 0,
        '전체_병원문의': item.totalStats?.hospitalInquiryCount || 0,
        '전체_응모률': formatPercent(item.totalStats?.entryRate),
        '시술환자_발송건수': item.smilePatient?.sendCount || 0,
        '시술환자_참여자수': item.smilePatient?.participantCount || 0,
        '시술환자_공유횟수': item.smilePatient?.shareCount || 0,
        '시술환자_후기작성자': item.totalStats?.reviewCompleteCount || 0,
        '시술환자_병원문의': item.smilePatient?.hospitalInquiryCount || 0,
        '시술환자_응모률': formatPercent(item.smilePatient?.entryRate),
        '시술환자_평균공유수': typeof item.smilePatient?.shareUserCount === 'number' ? item.smilePatient.shareUserCount.toFixed(1) : (item.smilePatient?.shareUserCount || 0),
        '환자친구_발송건수': item.patientFriend?.sendCount || 0,
        '환자친구_참여자수': item.patientFriend?.participantCount || 0,
        '환자친구_공유횟수': item.patientFriend?.shareCount || 0,
        // '환자친구_응모률': formatPercent(item.patientFriend?.entryRate),
        '환자친구_병원문의': item.patientFriend?.hospitalInquiryCount || 0,
        '환자친구_예약클릭': item.patientFriend?.bookingClickCount || 0,
        '환자친구_병원예약': item.patientFriend?.hospitalBookingCount || 0,
        '친구의친구_발송건수': item.friendOfFriend?.sendCount || 0,
        '친구의친구_참여자수': item.friendOfFriend?.participantCount || 0,
        '친구의친구_공유횟수': item.friendOfFriend?.shareCount || 0,
        // '친구의친구_응모률': formatPercent(item.friendOfFriend?.entryRate),
        '친구의친구_병원문의': item.friendOfFriend?.hospitalInquiryCount || 0,
        '친구의친구_예약클릭': item.friendOfFriend?.bookingClickCount || 0,
        '친구의친구_병원예약': item.friendOfFriend?.hospitalBookingCount || 0
      }));
      
      // 워크시트 생성
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // 컬럼 너비 설정
      const colWidths = Object.keys(excelData[0]).map(key => ({
        wch: Math.max(key.length, 12)
      }));
      ws['!cols'] = colWidths;
      
      // 워크북 생성
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '일자별통계');
      
      // 파일 다운로드
      const fileName = `일자별통계_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      message.success('엑셀 파일이 다운로드되었습니다.');
    };
    
    // 뒤로가기
    const goBack = () => {
      router.back();
    };
    
    // 마운트 시 실행
    onMounted(() => {
      fetchDailyStats();
    });
    
    return {
      loading,
      dailyStats,
      dateRange,
      columns,
      summaryData,
      formatDate,
      formatPercent,
      getEntryRateClass,
      getDayOfWeekClass,
      fetchDailyStats,
      handleDateChange,
      exportExcel,
      goBack
    };
  }
};
</script>

<style scoped lang="scss">
.titleContainer {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  width: 100%;
}

.page-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
}

.pageDesc {
  font-size: 14px;
  color: #6c757d;
  display: block;
  margin-top: 4px;
}

.header-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.date-filter {
  display: flex;
  gap: 8px;
  align-items: center;
  
  :deep(.ant-picker) {
    width: 280px;
  }
}

.back-button {
  display: flex;
  align-items: center;
  padding: 6px 16px;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  
  &:hover {
    color: #1890ff;
    border-color: #1890ff;
  }
}

.content-wrap-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  min-height: 500px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  
  p {
    margin-top: 16px;
    font-size: 14px;
    color: #6c757d;
  }
}

.table-container {
  :deep(.ant-table) {
    .ant-table-thead > tr > th {
      background: #fafafa;
      font-weight: 700;
      text-align: center;
      border-right: 1px solid #f0f0f0;
      position: sticky;
      top: 0;
      z-index: 10;
      
      &:last-child {
        border-right: none;
      }
    }
    
    .ant-table-summary {
      position: sticky;
      bottom: 0;
      z-index: 10;
      background: white;
    }
    
    .ant-table-tbody > tr > td {
      border-right: 1px solid #f0f0f0;
      
      &:last-child {
        border-right: none;
      }
    }
    
    .ant-table-tbody > tr:hover > td {
      background: #f5f8ff;
    }
  }
  
  .date-cell {
    font-weight: 700;
    
    &.weekday {
      color: #000000;
    }
    
    &.saturday {
      color: #1890ff;
    }
    
    &.sunday {
      color: #ff4d4f;
    }
  }
  
  .high-rate {
    color: #52c41a;
    font-weight: 600;
  }
  
  .medium-rate {
    color: #faad14;
    font-weight: 500;
  }
  
  .low-rate {
    color: #8c8c8c;
  }
  
  .summary-cell {
    background: #fafafa !important;
    strong {
      color: #000;
    }
  }
}

.no-data-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  
  .no-data-icon {
    font-size: 64px;
    color: #d9d9d9;
    margin-bottom: 24px;
  }
  
  h3 {
    font-size: 20px;
    font-weight: 500;
    color: #595959;
    margin-bottom: 8px;
  }
  
  p {
    font-size: 14px;
    color: #8c8c8c;
  }
}

@media screen and (max-width: 767px) {
  .title-row {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .header-actions {
    margin-top: 16px;
    width: 100%;
    flex-direction: column;
    align-items: stretch;
  }
  
  .date-filter {
    flex-direction: column;
    width: 100%;
    
    :deep(.ant-picker) {
      width: 100%;
    }
    
    .ant-btn {
      width: 100%;
    }
  }
  
  .back-button {
    width: 100%;
    justify-content: center;
  }
}
</style>